/**
 * Productivity Analytics (Jun 2026)
 * - Live Operations (Today) tab — unchanged
 * - Historical Reports tab — replaced by the premium HistoricalDashboard
 *   (single shared filter set, Month-to-Date default, all widgets in sync)
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart3, Info } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useAuth } from '../../App';
import LiveOperations from './LiveOperations';
import HistoricalDashboard from './HistoricalDashboard';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Workflow selector values (backend `order_type` param unchanged; 'typing' = Typing STAGE credit)
export const WORKFLOW_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'tax_search', label: 'Taxes' },
  { value: 'title_search', label: 'Title Search' },
  { value: 'typing', label: 'Typing' },
];

const ProductivityAnalytics = () => {
  const { user: currentUser } = useAuth();

  // TL-ORDER-TYPE-SCOPE (Jun 2026): Team Leaders only report on their
  // own workflow — Tax TLs get Tax Search, Title/Search TLs get Title Search.
  // TYPING-CREDIT-001 (Sep 2026): Typing is a separately selectable STAGE.
  // Admin: All | Taxes | Title Search | Typing. Title TL: Title Search | Typing. Tax TL: pinned.
  const isTaxTL = ['tax_team_leader', 'tax_search_team_leader'].includes(currentUser?.role);
  const isTitleTL = ['title_team_leader', 'title_search_team_leader'].includes(currentUser?.role);
  const teamOrderType = isTaxTL ? 'tax_search' : isTitleTL ? 'title_search' : null;
  const workflowOptions = isTaxTL ? null
    : isTitleTL ? [WORKFLOW_OPTIONS[2], WORKFLOW_OPTIONS[3]]
      : WORKFLOW_OPTIONS;

  const isFullAdmin = ['admin', 'hr_manager', 'team_leader', 'tax_team_leader',
                       'title_team_leader', 'title_search_team_leader', 'tax_search_team_leader',
                       'manager', 'supervisor'].includes(currentUser?.role);
  const isLimitedAccess = ['hr'].includes(currentUser?.role);
  const isAdmin = isFullAdmin || isLimitedAccess;
  const canExportReports = isFullAdmin; // HR Specialist cannot export reports

  // Live vs Historical view (deep-linkable via ?tab=live)
  const [activeView, setActiveView] = useState(() =>
    new URLSearchParams(window.location.search).get('tab') === 'live' ? 'live' : 'historical');

  // Users list for the Team Member filter (admin/leader scoped server-side).
  // Re-scoped to the selected workflow so Typing shows Typing Processors.
  const [usersWorkflow, setUsersWorkflow] = useState(() => new URLSearchParams(window.location.search).get('order_type') || teamOrderType || 'all');
  const [usersList, setUsersList] = useState([]);
  useEffect(() => {
    if (!isAdmin) return;
    const controller = new AbortController();
    const otParam = usersWorkflow && usersWorkflow !== 'all' ? `?order_type=${usersWorkflow}` : '';
    axios.get(`${API}/api/productivity-analytics/users-list${otParam}`, { signal: controller.signal })
      .then((res) => setUsersList(res.data?.users || []))
      .catch((err) => {
        if (!axios.isCancel(err)) console.error('Error fetching users list:', err);
      });
    return () => controller.abort();
  }, [isAdmin, usersWorkflow]);

  return (
    <div className="space-y-4" data-testid="productivity-analytics">
      {/* ANALYTICS-HEADER-001: page title/description now rendered by ReportsLayout */}
      {isLimitedAccess && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2 text-amber-800 text-sm">
          <Info className="w-4 h-4 flex-shrink-0" />
          <span>You have view-only access to productivity analytics. Contact HR Manager for report exports or target configuration.</span>
        </div>
      )}

      <Tabs value={activeView} onValueChange={setActiveView} data-testid="pa-view-tabs">
        <TabsList className="mb-2 h-auto rounded-xl bg-white border border-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,.04)] p-1 gap-0.5">
          <TabsTrigger value="live" data-testid="pa-tab-live" className="an-tab rounded-lg px-3.5 py-2 text-[13px] font-medium data-[state=active]:is-active data-[state=active]:bg-transparent data-[state=active]:shadow-none">Live Operations (Today)</TabsTrigger>
          <TabsTrigger value="historical" data-testid="pa-tab-historical" className="an-tab rounded-lg px-3.5 py-2 text-[13px] font-medium data-[state=active]:bg-transparent data-[state=active]:shadow-none">Historical Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="live" className="mt-2">
          <LiveOperations />
        </TabsContent>

        <TabsContent value="historical" className="mt-2">
          <HistoricalDashboard
            isAdmin={isAdmin}
            canExport={canExportReports}
            teamOrderType={teamOrderType}
            workflowOptions={workflowOptions}
            onWorkflowChange={setUsersWorkflow}
            users={usersList}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProductivityAnalytics;
